/**
 * I-1522 (docs/_archive/2026-10-31-concept-gates-history.md, WK-10): asks the network again for every module a screen's chunk
 * imports, so the reload Try again makes fetches what failed instead of failing it again.
 *
 * WebKit keeps a module script that failed to load in its memory cache, and that cache outlives a
 * reload of the page: measured on the production build, one of the create form's imports dropped
 * once, Try again reloaded the document, and the reloaded page failed the same import without a
 * request for it. A `fetch()` of the module's address replaces the record, after which the reload
 * draws the screen. WebKit's error names no module, so the failed one cannot be told apart: the
 * screen's chunk is read and every module it imports statically is asked for, recursively, from the
 * network. When one of them cannot be had, the reload would draw the same error, so the outcome says
 * so and Try again tells the reader instead of reloading.
 */

/** Modules read at most, so a graph that is not what it seems cannot keep the reader waiting. */
const MODULE_LIMIT = 400;

/** Static imports only: `from"./x.js"` and `import"./x.js"`, never `import("./x.js")`. */
const STATIC_IMPORT = /(?:\bfrom|\bimport)\s*(["'])([^"'\s]+)\1/g;

/**
 * The same-origin modules `source`, a module served at `base`, imports statically. Dev serves source
 * modules from the root (`/src/…`, `/node_modules/.vite/…`); the bundle imports its siblings
 * (`./OwnerFlowApi-DSkFuA8O.js`). A bare specifier or another origin is not this app's to refresh.
 */
export function staticImportsOf(source: string, base: string): string[] {
  const origin = new URL(base).origin;
  const found = new Set<string>();
  for (const match of source.matchAll(STATIC_IMPORT)) {
    const specifier = match[2];
    if (!/^(\.{1,2}\/|\/(?!\/))/.test(specifier)) continue;
    try {
      const url = new URL(specifier, base);
      if (url.origin === origin) found.add(url.href);
    } catch {
      // Not an address: a string in the code that only looked like an import.
    }
  }
  return [...found];
}

/**
 * What asking for one module came to: its text; `missing`, the server no longer has it (a 404 or
 * 410, or the SPA fallback's HTML where a script was asked for: the tab runs an old build); or
 * `failed`, no answer (the connection, a 5xx, the deadline).
 */
export type ModuleReadType = { text: string } | { missing: true } | { failed: true };

/** Asks for one module; never throws. */
export type ModuleReaderType = (url: string) => Promise<ModuleReadType>;

/**
 * `fresh`: every module came back, so a reload draws the screen. `missing`: one is gone from the
 * server, so a reload fetches the new build. `failed`: one could not be had, so a reload would draw
 * the same error, and the reader is told instead (I-1522).
 */
export type GraphRefreshType = {
  outcome: 'fresh' | 'missing' | 'failed';
  asked: string[];
};

/**
 * Asks for `root` and every module it imports statically, each once, level by level, and resolves
 * once every request has settled. A module that cannot be read is asked for and not descended into.
 */
export async function refreshModuleGraph(
  root: string,
  read: ModuleReaderType,
  limit = MODULE_LIMIT,
): Promise<GraphRefreshType> {
  const asked = new Set<string>([root]);
  let failed = false;
  let missing = false;
  let level = [root];
  while (level.length > 0) {
    const answers = await Promise.all(level.map((url) => read(url)));
    const next: string[] = [];
    answers.forEach((answer, index) => {
      if ('failed' in answer) failed = true;
      if ('missing' in answer) missing = true;
      if (!('text' in answer)) return;
      for (const url of staticImportsOf(answer.text, level[index])) {
        if (asked.has(url) || asked.size >= limit) continue;
        asked.add(url);
        next.push(url);
      }
    });
    level = next;
  }
  return { outcome: failed ? 'failed' : missing ? 'missing' : 'fresh', asked: [...asked] };
}

/**
 * The browser's reader. `cache: 'reload'` goes to the network for every module and stores the
 * answer, so nothing can be answered from the record of the failure, in the memory cache or the
 * HTTP cache. It costs the graph's modules again, once, on the reader's own Try again.
 */
export function browserModuleReader(signal?: AbortSignal): ModuleReaderType {
  return async (url) => {
    try {
      const response = await fetch(url, { signal, cache: 'reload', credentials: 'same-origin' });
      if (response.status === 404 || response.status === 410) return { missing: true };
      if (!response.ok) return { failed: true };
      if ((response.headers.get('content-type') ?? '').includes('text/html')) {
        return { missing: true };
      }
      return { text: await response.text() };
    } catch {
      return { failed: true };
    }
  };
}
